<template>
  <div class="min-h-screen bg-[#f6f7fb] py-6">
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4">
      <section class="rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Internal Tool</p>
            <h1 class="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Manual Company Jobs</h1>
            <p class="mt-2 max-w-2xl text-sm text-slate-500">
              Add and curate external jobs without going through the current admin auth flow.
            </p>
          </div>

          <div class="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[320px]">
            <label class="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</label>
            <select
              v-model="selectedCompanyId"
              class="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              @change="handleCompanyChange"
            >
              <option value="">Select a company</option>
              <option
                v-for="company in companies"
                :key="company.id"
                :value="String(company.id)"
              >
                {{ company.name }}{{ company.manual_jobs_count ? ` (${company.manual_jobs_count})` : '' }}
              </option>
            </select>
          </div>
        </div>
      </section>

      <section
        v-if="selectedCompany"
        class="rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-slate-900">{{ selectedCompany.name }}</h2>
            <p class="mt-1 text-sm text-slate-500">
              {{ selectedCompany.industry_name || 'Industry not set' }} · {{ visibleJobsCount }} visible jobs
            </p>
          </div>

          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            @click="addNewJob"
          >
            Add job
          </button>
        </div>
      </section>

      <section
        v-if="selectedCompanyId && !loading && jobs.length === 0"
        class="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
      >
        <h3 class="text-lg font-semibold text-slate-900">No jobs yet</h3>
        <p class="mt-2 text-sm text-slate-500">Create the first job for this company and it will appear here.</p>
      </section>

      <section
        v-if="!selectedCompanyId && !loading"
        class="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
      >
        <h3 class="text-lg font-semibold text-slate-900">Pick a company to start</h3>
        <p class="mt-2 text-sm text-slate-500">This page is intentionally open for now, so select a company and manage its jobs directly.</p>
      </section>

      <section v-if="loading" class="rounded-3xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm">
        <p class="text-sm text-slate-500">Loading jobs…</p>
      </section>

      <section v-if="selectedCompanyId && jobs.length > 0" class="flex flex-col gap-4">
        <article
          v-for="(job, index) in jobs"
          :key="job.localKey"
          class="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            type="button"
            class="flex w-full items-start justify-between gap-4 px-6 py-5 text-left transition hover:bg-slate-50"
            @click="toggleExpanded(job.localKey)"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                  :class="job.form.is_visible ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'">
                  {{ job.form.is_visible ? 'Visible' : 'Hidden' }}
                </span>
                <span class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  #{{ index + 1 }}
                </span>
                <span
                  v-if="job.id && !job.form.category"
                  class="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700"
                >
                  No category
                </span>
              </div>
              <h3 class="mt-3 text-lg font-semibold text-slate-900">
                {{ job.form.title || 'Untitled job' }}
              </h3>
              <p class="mt-1 text-sm text-slate-500">
                {{ buildMetaLine(job.form) }}
              </p>
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <button
                type="button"
                class="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                :disabled="index === 0 || job.isSaving"
                @click.stop="moveJob(index, -1)"
              >
                Up
              </button>
              <button
                type="button"
                class="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                :disabled="index === jobs.length - 1 || job.isSaving"
                @click.stop="moveJob(index, 1)"
              >
                Down
              </button>
              <span class="rounded-lg border border-slate-200 px-2 py-1 text-slate-400">
                ≡
              </span>
            </div>
          </button>

          <div v-if="job.isExpanded" class="border-t border-slate-200 px-6 py-6">
            <div class="mb-5">
              <span class="mb-2 block text-sm font-medium text-slate-700">Source <span class="text-rose-500">*</span></span>
              <div class="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
                <button
                  v-for="source in sourceOptions"
                  :key="source.value"
                  type="button"
                  class="rounded-xl px-3 py-2 text-sm font-semibold transition"
                  :class="job.form.source_origin === source.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                  @click="job.form.source_origin = source.value"
                >
                  {{ source.label }}
                </button>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <label class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Job title <span class="text-rose-500">*</span></span>
                <input v-model="job.form.title" type="text" class="field" placeholder="Senior Geologist" />
              </label>

              <div class="grid gap-4 md:col-span-2 md:grid-cols-3">
                <label class="block">
                  <span class="mb-2 block text-sm font-medium text-slate-700">Industry <span class="text-rose-500">*</span></span>
                  <select v-model="job.form.industry_id" class="field" @change="setIndustry(job)">
                    <option value="">Select industry</option>
                    <option v-for="industry in classifications" :key="industry.id" :value="String(industry.id)">
                      {{ industry.name }}
                    </option>
                  </select>
                </label>

                <label class="block">
                  <span class="mb-2 block text-sm font-medium text-slate-700">Category <span class="text-rose-500">*</span></span>
                  <select
                    v-model="job.form.category"
                    class="field"
                    :disabled="!job.form.industry_id"
                    @change="job.form.role_id = ''"
                  >
                    <option value="">{{ job.form.industry_id ? 'Select category' : 'Select an industry first' }}</option>
                    <option v-for="category in getCategoryOptions(job.form)" :key="category" :value="category">
                      {{ category }}
                    </option>
                  </select>
                </label>

                <label class="block">
                  <span class="mb-2 block text-sm font-medium text-slate-700">
                    Role <span v-if="job.form.category !== otherCategory" class="text-rose-500">*</span>
                  </span>
                  <select
                    v-model="job.form.role_id"
                    class="field"
                    :disabled="getRoleOptions(job.form).length === 0"
                  >
                    <option value="">{{ getRolePlaceholder(job.form) }}</option>
                    <option v-for="role in getRoleOptions(job.form)" :key="role.id" :value="String(role.id)">
                      {{ role.name }}
                    </option>
                  </select>
                </label>
              </div>

              <label class="block md:col-span-2">
                <span class="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-sm font-medium text-slate-700">
                  <span>Summary</span>
                  <span
                    class="text-xs font-semibold"
                    :class="getSummaryWordCount(job.form.summary) > 150 ? 'text-amber-600' : 'text-slate-500'"
                  >
                    {{ getSummaryWordCount(job.form.summary) }} / 150 words recommended
                  </span>
                </span>
                <textarea
                  v-model="job.form.summary"
                  class="field summary-field"
                  rows="5"
                  placeholder="Briefly describe the role, scope, team, and what makes it appealing."
                />
              </label>

              <label class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Location <span class="text-rose-500">*</span></span>
                <input v-model="job.form.location" type="text" class="field" placeholder="Perth, WA" />
              </label>

              <label class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Employment type <span class="text-rose-500">*</span></span>
                <select v-model="job.form.employment_type" class="field">
                  <option value="">Select type</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Contract">Contract</option>
                </select>
              </label>

              <div class="block md:col-span-2">
                <div class="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-sm font-medium text-slate-700">
                  <span>Map location</span>
                  <button
                    v-if="hasMapLocation(job.form)"
                    type="button"
                    class="text-xs font-semibold text-slate-500 hover:text-rose-600"
                    @click="clearMapLocation(job)"
                  >
                    Remove pin
                  </button>
                </div>
                <GoogleMap
                  v-if="job.form.location.trim()"
                  :key="`${job.localKey}-map`"
                  editable
                  :address="job.form.location"
                  :lat="job.form.location_lat"
                  :lng="job.form.location_lng"
                  map-height-class="h-56"
                  @update:location="setMapLocation(job, $event)"
                  @geocode-failed="clearMapLocation(job)"
                />
                <p v-else class="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                  Enter a location to place the job on the map.
                </p>
                <p v-if="job.form.location.trim() && !hasMapLocation(job.form)" class="mt-2 text-xs text-amber-600">
                  No pin yet. This job won't appear in the jobs map view.
                </p>
              </div>

              <label class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Roster</span>
                <input v-model="job.form.roster" type="text" class="field" placeholder="8:6" />
              </label>

              <div class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Compensation</span>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="option in compensationOptions"
                    :key="option.value"
                    type="button"
                    class="rounded-xl border px-3 py-2 text-sm font-semibold transition"
                    :class="job.form.compensation_mode === option.value ? 'border-slate-900 bg-slate-900 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900'"
                    @click="setCompensationMode(job, option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <label
                v-if="job.form.compensation_mode"
                class="block"
                :class="{ 'md:col-span-2': job.form.compensation_mode === 'salary' }"
              >
                <span class="mb-2 block text-sm font-medium text-slate-700">
                  {{ job.form.compensation_mode === 'salary' ? 'Salary amount' : 'Rate amount' }}
                </span>
                <div class="relative">
                  <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">$</span>
                  <input
                    v-model="job.form.compensation_amount"
                    type="text"
                    inputmode="numeric"
                    class="field compensation-field"
                    :placeholder="job.form.compensation_mode === 'salary' ? '140000' : '850'"
                    @input="formatCompensationAmount(job)"
                  />
                </div>
              </label>

              <label v-if="job.form.compensation_mode === 'rate'" class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Rate type</span>
                <select v-model="job.form.compensation_rate_type" class="field">
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </select>
              </label>

              <label class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Date posted <span class="text-rose-500">*</span></span>
                <input v-model="job.form.date_posted" type="date" class="field" />
              </label>

              <label class="block">
                <span class="mb-2 block text-sm font-medium text-slate-700">Closing date <span class="text-rose-500">*</span></span>
                <input v-model="job.form.closing_date" type="date" class="field" />
              </label>

              <label class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Job URL <span class="text-rose-500">*</span></span>
                <input v-model="job.form.job_url" type="url" class="field" placeholder="https://company.com/jobs/123" />
              </label>
            </div>

            <label class="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input v-model="job.form.is_visible" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
              <span class="text-sm text-slate-700">Show this job to users</span>
            </label>

            <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                class="rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                :disabled="job.isSaving"
                @click="removeJob(job, index)"
              >
                Delete
              </button>

              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  :disabled="job.isSaving"
                  @click="toggleExpanded(job.localKey)"
                >
                  Collapse
                </button>
                <button
                  type="button"
                  class="rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="job.isSaving"
                  @click="saveJob(job)"
                >
                  {{ job.isSaving ? 'Saving…' : 'Save job' }}
                </button>
              </div>
            </div>
          </div>
        </article>
      </section>
    </div>
  </div>
</template>

<script>
import manualJobsService from '@/services/marketplace/manualJobs.service'
import GoogleMap from '@/components/marketplace/generic/GoogleMap.vue'

const OTHER_CATEGORY = 'Other'

// Roles that have no category yet (e.g. new industries) are filed under "Other".
const getRoleCategory = (role) => role.category_name || OTHER_CATEGORY

const createBlankForm = () => ({
  title: '',
  summary: '',
  location: '',
  location_lat: null,
  location_lng: null,
  employment_type: '',
  source_origin: 'linkedin',
  roster: '',
  compensation_mode: '',
  compensation_amount: '',
  compensation_rate_type: 'hour',
  date_posted: '',
  closing_date: '',
  job_url: '',
  is_visible: true,
})

const normalizeJob = (job) => ({
  id: job.id ?? null,
  localKey: `job-${job.id ?? Math.random().toString(36).slice(2)}`,
  isExpanded: false,
  isSaving: false,
  form: {
    title: job.title ?? '',
    summary: job.summary ?? '',
    location: job.location ?? '',
    location_lat: toCoordinate(job.location_lat),
    location_lng: toCoordinate(job.location_lng),
    employment_type: job.employment_type ?? '',
    source_origin: job.source_origin ?? '',
    roster: job.roster ?? '',
    compensation_mode: job.compensation_mode ?? '',
    compensation_amount: normalizeAmountDisplay(job.compensation_amount ?? ''),
    compensation_rate_type: job.compensation_rate_type ?? 'hour',
    date_posted: job.date_posted ?? '',
    closing_date: job.closing_date ?? '',
    job_url: job.job_url ?? '',
    industry_id: '',
    category: job.category ?? '',
    subcategory: job.subcategory ?? '',
    role_id: '',
    is_visible: Number(job.is_visible ?? 1) === 1,
  },
})

const toCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const normalizeAmountDisplay = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '')
  if (!digits) return ''

  return new Intl.NumberFormat('en-AU').format(Number(digits))
}

export default {
  name: 'AdminManualJobs',
  components: { GoogleMap },
  inject: ['toggleLoading'],
  data() {
    return {
      loading: false,
      companies: [],
      selectedCompanyId: this.$route.params.companyId ?? '',
      selectedCompany: null,
      jobs: [],
      classifications: [],
      otherCategory: OTHER_CATEGORY,
      sourceOptions: [
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'seek', label: 'SEEK' },
        { value: 'other', label: 'Other' },
      ],
      compensationOptions: [
        { value: 'salary', label: 'Salary' },
        { value: 'rate', label: 'Rate' },
      ],
    }
  },
  computed: {
    visibleJobsCount() {
      return this.jobs.filter((job) => job.form.is_visible).length
    }
  },
  watch: {
    '$route.params.companyId'(nextCompanyId) {
      this.selectedCompanyId = nextCompanyId ?? ''
      if (this.selectedCompanyId) {
        this.fetchJobs()
      } else {
        this.selectedCompany = null
        this.jobs = []
      }
    }
  },
  methods: {
    async fetchCompanies() {
      const response = await manualJobsService.getCompanies()
      this.companies = response.data
      this.syncSelectedCompany()
    },
    syncSelectedCompany() {
      const companyId = Number(this.selectedCompanyId)
      this.selectedCompany = this.companies.find((company) => Number(company.id) === companyId) ?? null
    },
    async fetchClassifications() {
      const response = await manualJobsService.getClassifications()
      this.classifications = response.data ?? []
    },
    toJob(data) {
      const job = normalizeJob(data)
      const companyIndustryId = String(this.selectedCompany?.industry_id ?? '')
      const { category, subcategory } = job.form

      // Prefer the company's industry when the same category/role names exist in several industries.
      const industries = [...this.classifications].sort((a, b) =>
        Number(String(b.id) === companyIndustryId) - Number(String(a.id) === companyIndustryId)
      )
      const role = industries
        .flatMap((industry) => industry.roles.map((item) => ({ ...item, industryId: industry.id })))
        .find((item) => getRoleCategory(item) === category && item.name === subcategory)

      if (role) {
        job.form.industry_id = String(role.industryId)
        job.form.role_id = String(role.id)
        return job
      }

      // "Other", unclassified, or no longer active: start from the company's industry, keeping only "Other".
      if (category !== OTHER_CATEGORY) {
        job.form.category = ''
      }
      job.form.industry_id = this.classifications.some((industry) => String(industry.id) === companyIndustryId)
        ? companyIndustryId
        : ''

      return job
    },
    getIndustryRoles(form) {
      return this.classifications.find((industry) => String(industry.id) === String(form.industry_id))?.roles ?? []
    },
    getCategoryOptions(form) {
      const categories = this.getIndustryRoles(form).map((role) => role.category_name).filter(Boolean)
      return [...new Set(categories), OTHER_CATEGORY]
    },
    getRoleOptions(form) {
      if (!form.category) return []
      return this.getIndustryRoles(form).filter((role) => getRoleCategory(role) === form.category)
    },
    getRolePlaceholder(form) {
      if (!form.category) return 'Select a category first'
      if (form.category !== OTHER_CATEGORY) return 'Select role'
      return this.getRoleOptions(form).length ? 'No specific role' : 'No roles for Other'
    },
    hasMapLocation(form) {
      return form.location_lat !== null && form.location_lng !== null
    },
    setMapLocation(job, { lat, lng }) {
      job.form.location_lat = lat
      job.form.location_lng = lng
    },
    clearMapLocation(job) {
      job.form.location_lat = null
      job.form.location_lng = null
    },
    setIndustry(job) {
      job.form.category = ''
      job.form.role_id = ''
    },
    async fetchJobs() {
      if (!this.selectedCompanyId) {
        this.jobs = []
        this.selectedCompany = null
        return
      }

      this.loading = true
      this.toggleLoading(true)

      try {
        const response = await manualJobsService.getJobs(this.selectedCompanyId)
        this.selectedCompany = response.data.company
        this.jobs = (response.data.jobs ?? []).map((job) => this.toJob(job))
      } catch (error) {
        this.$root.notify('Failed to load manual jobs for this company.', 'error')
      } finally {
        this.loading = false
        this.toggleLoading(false)
      }
    },
    handleCompanyChange() {
      if (!this.selectedCompanyId) {
        this.$router.push({ name: 'AdminManualJobs' })
        return
      }

      this.$router.push({ name: 'AdminManualJobsCompany', params: { companyId: this.selectedCompanyId } })
    },
    addNewJob() {
      const nextJob = this.toJob(createBlankForm())
      nextJob.isExpanded = true
      this.jobs.unshift(nextJob)
    },
    toggleExpanded(localKey) {
      this.jobs = this.jobs.map((job) =>
        job.localKey === localKey ? { ...job, isExpanded: !job.isExpanded } : job
      )
    },
    buildMetaLine(form) {
      const sourceLabel = form.source_origin === 'linkedin' ? 'LinkedIn' : form.source_origin === 'seek' ? 'SEEK' : form.source_origin === 'other' ? 'Other' : ''
      const compensation = this.formatCompensationPreview(form)
      const parts = [form.category, form.location, form.employment_type, sourceLabel, compensation, form.date_posted ? `Posted ${form.date_posted}` : 'No posted date']
      return parts.filter(Boolean).join(' · ') || 'No details yet'
    },
    getSummaryWordCount(summary) {
      return String(summary ?? '').trim().split(/\s+/).filter(Boolean).length
    },
    setCompensationMode(job, mode) {
      if (job.form.compensation_mode === mode) {
        job.form.compensation_mode = ''
        job.form.compensation_amount = ''
        job.form.compensation_rate_type = 'hour'
        return
      }

      job.form.compensation_mode = mode
      job.form.compensation_amount = ''
      job.form.compensation_rate_type = 'hour'
    },
    formatCompensationPreview(form) {
      const amount = Number(String(form.compensation_amount).replace(/[^\d]/g, ''))
      if (!Number.isFinite(amount) || amount <= 0) {
        return ''
      }

      const formatted = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
      }).format(amount)

      if (form.compensation_mode === 'salary') {
        return formatted
      }

      if (form.compensation_mode === 'rate') {
        const labelMap = { hour: 'hour', day: 'day', month: 'month' }
        return `${formatted} / ${labelMap[form.compensation_rate_type] || form.compensation_rate_type}`
      }

      return ''
    },
    formatCompensationAmount(job) {
      job.form.compensation_amount = normalizeAmountDisplay(job.form.compensation_amount)
    },
    async saveJob(job) {
      if (!this.selectedCompanyId) return
      if (!job.form.title.trim()) {
        this.$root.notify('Job title is required.', 'error')
        return
      }
      if (!job.form.location.trim()) {
        this.$root.notify('Location is required.', 'error')
        return
      }
      if (!job.form.employment_type.trim()) {
        this.$root.notify('Employment type is required.', 'error')
        return
      }
      if (!job.form.source_origin.trim()) {
        this.$root.notify('Source is required.', 'error')
        return
      }
      if (!job.form.industry_id || !job.form.category) {
        this.$root.notify('Industry and category are required.', 'error')
        return
      }
      if (job.form.category !== OTHER_CATEGORY && !job.form.role_id) {
        this.$root.notify('Role is required.', 'error')
        return
      }
      if (!job.form.date_posted.trim()) {
        this.$root.notify('Date posted is required.', 'error')
        return
      }
      if (!job.form.closing_date.trim()) {
        this.$root.notify('Job closing date is required.', 'error')
        return
      }
      if (!job.form.job_url.trim()) {
        this.$root.notify('Job URL is required.', 'error')
        return
      }

      job.isSaving = true

      try {
        const payload = {
          ...job.form,
          compensation_amount: String(job.form.compensation_amount).replace(/[^\d]/g, ''),
          is_visible: job.form.is_visible ? 1 : 0,
        }

        const response = job.id
          ? await manualJobsService.updateJob(this.selectedCompanyId, job.id, payload)
          : await manualJobsService.createJob(this.selectedCompanyId, payload)

        const normalized = this.toJob(response.data)
        normalized.isExpanded = Boolean(job.id)

        if (job.id) {
          this.jobs = this.jobs.map((item) => item.localKey === job.localKey ? normalized : item)
        } else {
          this.jobs = [normalized, ...this.jobs.filter((item) => item.localKey !== job.localKey)]
        }
        await this.fetchCompanies()
        this.$root.notify('Job saved.', 'success')
      } catch (error) {
        const message = error?.response?.data?.error || 'Failed to save job.'
        this.$root.notify(message, 'error')
        job.isSaving = false
      }
    },
    async removeJob(job, index) {
      if (!job.id) {
        this.jobs.splice(index, 1)
        return
      }

      if (!window.confirm('Delete this job?')) return

      try {
        await manualJobsService.deleteJob(this.selectedCompanyId, job.id)
        this.jobs.splice(index, 1)
        await this.fetchCompanies()
        this.$root.notify('Job deleted.', 'success')
      } catch (error) {
        this.$root.notify('Failed to delete job.', 'error')
      }
    },
    async moveJob(index, direction) {
      const targetIndex = index + direction

      if (targetIndex < 0 || targetIndex >= this.jobs.length) {
        return
      }

      const reordered = [...this.jobs]
      const [movedJob] = reordered.splice(index, 1)
      reordered.splice(targetIndex, 0, movedJob)
      this.jobs = reordered

      const persistedJobIds = reordered
        .filter((job) => job.id)
        .map((job) => job.id)

      if (persistedJobIds.length === 0) {
        return
      }

      try {
        const response = await manualJobsService.reorderJobs(this.selectedCompanyId, persistedJobIds)
        const expandedMap = new Map(this.jobs.map((job) => [job.id, job.isExpanded]))
        this.jobs = (response.data.jobs ?? []).map((job) => {
          const normalized = this.toJob(job)
          normalized.isExpanded = expandedMap.get(job.id) ?? false
          return normalized
        })
      } catch (error) {
        this.$root.notify('Failed to reorder jobs.', 'error')
        await this.fetchJobs()
      }
    }
  },
  async created() {
    try {
      this.toggleLoading(true)
      await Promise.all([this.fetchCompanies(), this.fetchClassifications()])
      if (this.selectedCompanyId) {
        await this.fetchJobs()
      }
    } finally {
      this.toggleLoading(false)
    }
  }
}
</script>

<style scoped>
.field {
  @apply h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100;
}

.summary-field {
  @apply h-auto min-h-[8rem] py-3 leading-relaxed;
}

.compensation-field {
  padding-left: 2rem;
}
</style>
